import { OrthoPalettes } from "@nova/constants"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import {
    selectAllInfraredFlightsMap,
    selectOrthoFlightsMap,
} from "features/entity-caching/flights/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { selectInfraredJobByFlightId } from "plugins/InfraredImage/InfraredImageSlice"
import { CogOrthoLayer } from "./CogOrthoLayer"
import { LegacyOrthoLayer } from "./LegacyOrthoLayer"
import { selectOrthoComparison, directionToClip } from "../OrthoComparisonSlice"
import { selectOrthoJobByFlightId } from "../OrthomosaicSlice"

export function useOrthoComparisonBoostLayer() {
    const comparison = useAppSelector(selectOrthoComparison)
    const orthoFlights = useAppSelector(selectOrthoFlightsMap)
    const irFlights = useAppSelector(selectAllInfraredFlightsMap)
    const fireId = useAppSelector(selectActivatedFireId)
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const state = store.getState()

    const { flightId, direction, threshold, comparisonOpacity } = comparison
    if (!flightId || threshold === null) return []

    const comparisonClip = directionToClip(direction, threshold)
    const opacity = comparisonOpacity / 100

    const orthoFlight = orthoFlights[flightId]
    if (orthoFlight) {
        const job = selectOrthoJobByFlightId(flightId)(state)
        const shared = {
            id: `Orthomosaic-boost:${flightId}`,
            bbox: orthoFlight.orthoBoundingBox,
            maxZoom: job?.maxZoom || 21,
            minZoom: job?.minZoom || 8,
            opacity,
            comparisonClip,
        }
        const extraFeatures = orthoFlight.job?.extraFeatures
        const cogId = orthoFlight.cogOrtho?.fileId

        if (!extraFeatures?.includes("cog"))
            return [
                LegacyOrthoLayer({
                    ...shared,
                    fileroot: `/api/organization/${workspaceId}/project/${fireId}/chapter/asd/flight/${orthoFlight.demoId || flightId}/orthomosaicTiles`,
                }),
            ]
        if (extraFeatures.includes("cogId") && !cogId) return []

        return [
            CogOrthoLayer({
                ...shared,
                orgId: workspaceId || "",
                projectId: fireId || "",
                flightId: orthoFlight.demoId || flightId,
                isDemo: !!orthoFlight.demoId,
                cogId,
            }),
        ]
    }

    const irFlight = irFlights[flightId]
    if (irFlight) {
        const job = selectInfraredJobByFlightId(flightId)(state)
        const shared = {
            id: `IR-Orthomosaic-boost:${flightId}`,
            bbox: irFlight.orthoBoundingBox,
            maxZoom: job?.maxZoom || 21,
            minZoom: job?.minZoom || 8,
            opacity,
            comparisonClip,
            palette: irFlight.orthoGradient || OrthoPalettes.WhiteHot,
        }
        const extraFeatures = irFlight.job?.extraFeatures
        const cogId = irFlight.cogOrtho?.fileId

        if (!extraFeatures?.includes("cog"))
            return [
                LegacyOrthoLayer({
                    ...shared,
                    fileroot: `/api/organization/${workspaceId}/project/${fireId}/chapter/asd/flight/${irFlight.demoId || flightId}/orthomosaicTiles`,
                }),
            ]
        if (extraFeatures.includes("cogId") && !cogId) return []

        return [
            CogOrthoLayer({
                ...shared,
                orgId: workspaceId || "",
                projectId: fireId || "",
                flightId: irFlight.demoId || flightId,
                isDemo: !!irFlight.demoId,
                cogId,
            }),
        ]
    }

    return []
}
